import React from "react";
import { CustomerDetailModal } from "./CustomerDetailModal";
import { CustomerFormModal } from "./CustomerFormModal";
import { BulkUploadModal } from "./BulkUploadModal";
import AllocationModal from "./AllocationModal";
import { Modal, DataTable as Table, Avatar } from "@/components/ui";
import { CONTACT_STAGES } from "../constants/contactConstants";
import { Calendar, User, Download } from "lucide-react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

import { ContactStatusModal } from "./ContactStatusModal";

/**
 * ContactModals
 * Consolidates all modals used in the Contacts feature to keep the main screen clean.
 */
export const ContactModals = ({
  // Status Modal Props
  pendingStatus,
  setPendingStatus,
  confirmStatusUpdate,

  // Detail Modal Props
  detail,
  setDetail,
  activities,
  actNote,
  setActNote,
  onAddActivity,
  updateStatus,

  // Add/Edit Modal Props
  addOpen,
  setAddOpen,
  editOpen,
  setEditOpen,
  editData,
  setEditData,

  // Bulk Upload Props
  bulkOpen,
  setBulkOpen,
  bulkFile,
  setBulkFile,
  bulkImport,
  filteredLeads,

  // Allocation Props
  assignModalOpen,
  setAssignModalOpen,
  loadData,

  // Import History Details
  selectedImport,
  setSelectedImport,
  stages,

  // Auth/Permissions
  user,
  users
}) => {
  return (
    <>
      {/* 1. Customer Detail & Interaction Timeline */}
      {detail && (
        <CustomerDetailModal
          isOpen={!!detail}
          detail={detail}
          onClose={() => setDetail(null)}
          activities={activities}
          actNote={actNote}
          setActNote={setActNote}
          onAddActivity={onAddActivity}
          updateStatus={updateStatus}
          setDetail={setDetail}
          stages={CONTACT_STAGES}
          users={users || []}
          user={user}
        />
      )}

      {/* 2. Add Customer Form */}
      <CustomerFormModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        stages={CONTACT_STAGES}
        mode="add"
        currentUser={user}
      />

      {/* 3. Edit Customer Form */}
      {editOpen && (
        <CustomerFormModal
          isOpen={editOpen}
          onClose={() => {
            setEditOpen(false);
            setEditData(null);
          }}
          initialData={editData}
          stages={CONTACT_STAGES}
          mode="edit"
          currentUser={user}
        />
      )}

      {/* 4. Bulk Customer Upload */}
      <BulkUploadModal
        isOpen={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
          setBulkFile(null);
        }}
        onUpload={(customers, filename) => bulkImport({ contacts: customers, filename })}
        existingCustomers={filteredLeads}
        initialFile={bulkFile}
      />

      {/* 5. Batch Allocation Modal */}
      {assignModalOpen && (
        <AllocationModal
          isOpen={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          onSuccess={() => {
            loadData();
            setAssignModalOpen(false);
          }}
        />
      )}

      {/* 6. Import Details Modal */}
      {selectedImport && (
        <Modal
          isOpen={!!selectedImport}
          onClose={() => setSelectedImport(null)}
          title={`File Details: ${selectedImport.filename}`}
          size="max-w-4xl"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-100">
              <div className="flex items-center gap-4 text-xs font-bold text-slate-500 uppercase tracking-widest">
                <div className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(selectedImport.createdAt).toLocaleString()}</div>
                <div className="flex items-center gap-1.5"><User size={14} /> {selectedImport.importedBy?.name || "System"}</div>
              </div>
              <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">{selectedImport.customers?.length || 0} Customers Found</div>
            </div>

            <div className="border border-slate-100 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto shadow-sm bg-white">
              {selectedImport.customers?.length > 0 ? (
                <Table headers={["ID & Name", "Contact Details", "Address", "Status"]}>
                  {selectedImport.customers.map(c => (
                    <tr key={c._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="td p-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={c.customerName || c.firstName} size="w-7 h-7 text-[10px]" />
                          <div>
                            <div className="text-xs font-bold text-brand-dark">{c.customerName || `${c.firstName} ${c.lastName}`}</div>
                            <div className="text-[9px] text-brand-blue font-bold uppercase">{(c.customId || "").toUpperCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td p-3">
                        <div className="text-xs font-medium text-slate-600">{c.phoneNumber}</div>
                        <div className="text-[10px] text-slate-400">{c.email || "No email"}</div>
                      </td>
                      <td className="td p-3 text-xs text-slate-600">
                        {c.city}, {c.state}
                      </td>
                      <td className="td p-3">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-800 uppercase tracking-tighter">
                          {(c.status || "New").replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </Table>
              ) : (
                <div className="p-12 text-center text-slate-400 italic text-sm">No customer records matching this import session were found.</div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => {
                  const data = selectedImport.customers.map(c => ({
                    'Customer ID': (c.customId || "").toUpperCase(),
                    'Full Name': c.customerName || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
                    'Phone': c.phoneNumber || c.phone || "",
                    'Email': c.email || 'N/A',
                    'City': c.city || "",
                    'State': c.state || "",
                    'Status': (c.status || "New").replace(/_/g, ' ')
                  }));
                  const ws = XLSX.utils.json_to_sheet(data);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "Customers");
                  XLSX.writeFile(wb, selectedImport.filename || `import_${selectedImport._id}.xlsx`);
                  toast.success("Download started");
                }}
                className="btn-secondary px-6 py-2 flex items-center gap-2 text-xs font-bold"
              >
                <Download size={14} /> Download File
              </button>
              <button
                onClick={() => setSelectedImport(null)}
                className="btn-primary px-8 py-2 text-xs font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
      <ContactStatusModal
        isOpen={!!pendingStatus}
        onClose={() => setPendingStatus(null)}
        onConfirm={confirmStatusUpdate}
        pendingStatus={pendingStatus}
        stages={stages}
      />
    </>
  );
};
